import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Users, HeartHandshake, ArrowRight, Check } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion } from 'framer-motion';

const Register = () => {
  const { isRTL, t } = useLanguage();

  const studentBenefits = [
    t('auth.register.studentBenefit1'),
    t('auth.register.studentBenefit2'),
    t('auth.register.studentBenefit3'),
    t('auth.register.studentBenefit4'),
  ];

  const teacherBenefits = [
    t('auth.register.teacherBenefit1'),
    t('auth.register.teacherBenefit2'),
    t('auth.register.teacherBenefit3'),
    t('auth.register.teacherBenefit4'),
  ];

  const parentBenefits = [
    t('auth.register.parentBenefit1'),
    t('auth.register.parentBenefit2'),
    t('auth.register.parentBenefit3'),
    t('auth.register.parentBenefit4'),
  ];

  return (
    <Layout>
      <SEO titleKey="seo.register.title" descriptionKey="seo.register.description" noindex={true} />
      <div className="min-h-[calc(100vh-200px)] flex items-center bg-background py-16" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="absolute inset-0 islamic-pattern-overlay opacity-20 pointer-events-none" />

        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
              {t('auth.register.heading')}
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {t('auth.register.subheading')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* Student Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
            >
              <div className="bg-card rounded-xl border border-border p-8 h-full flex flex-col hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 group">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary/15 transition-colors">
                  <Users className="h-7 w-7 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {t('auth.register.asStudent')}
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('auth.register.studentDesc')}
                </p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {studentBenefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                      <Check className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full rounded-lg h-11 font-semibold">
                  <Link to="/register/student" className="flex items-center justify-center gap-2">
                    {t('auth.register.asStudent')}
                    <ArrowRight className="h-4 w-4 rtl-mirror" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Teacher Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <div className="bg-card rounded-xl border border-border p-8 h-full flex flex-col hover:shadow-soft-lg hover:border-amber-500/30 transition-all duration-300 group">
                <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center mb-5 group-hover:bg-amber-500/15 transition-colors">
                  <GraduationCap className="h-7 w-7 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {t('auth.register.asTeacher')}
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('auth.register.teacherDesc')}
                </p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {teacherBenefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                      <Check className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full rounded-lg h-11 font-semibold bg-amber-500 hover:bg-amber-500/90 text-amber-950">
                  <Link to="/register/teacher" className="flex items-center justify-center gap-2">
                    {t('auth.register.asTeacher')}
                    <ArrowRight className="h-4 w-4 rtl-mirror" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Parent Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="bg-card rounded-xl border border-border p-8 h-full flex flex-col hover:shadow-soft-lg hover:border-emerald-500/30 transition-all duration-300 group">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-5 group-hover:bg-emerald-500/15 transition-colors">
                  <HeartHandshake className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">
                  {t('auth.register.asParent')}
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  {t('auth.register.parentDesc')}
                </p>
                <ul className="space-y-2.5 mb-8 flex-1">
                  {parentBenefits.map((b, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/80">
                      <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
                <Button asChild className="w-full rounded-lg h-11 font-semibold bg-emerald-600 hover:bg-emerald-600/90 text-white">
                  <Link to="/register/parent" className="flex items-center justify-center gap-2">
                    {t('auth.register.asParent')}
                    <ArrowRight className="h-4 w-4 rtl-mirror" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              {t('auth.register.haveAccount')}
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t('auth.register.signIn')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
